import React from 'react';
import { Modal, Button, Card, CardContent, CardHeader, CardTitle } from '../../../shared/components';
import { User,Ticket, Clock, X} from 'lucide-react';

/**
 * Modal para mostrar detalles de operaciones agrupadas por operationId
 */
const SeatDetailsModal = ({ isOpen, onClose, operation }) => {
  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatear precio
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  // Obtener color del tipo de operación
  const getOperationTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'sale':
        return 'text-green-600';
      case 'return':
      case 'return_admin':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Obtener texto del tipo de operación
  const getOperationTypeText = (type) => {
    switch (type?.toLowerCase()) {
      case 'sale':
        return 'Venta';
      case 'return':
      case 'return_admin':
        return 'Devolución';
      default:
        return type || 'N/A';
    }
  };

  const formatSeatId = (compositeId) => {
    // Convertir "PB-1-2" a "Planta Baja - Fila 1 - Asiento 2"
    const parts = compositeId.split('-');
    if (parts.length === 3) {
      const section = parts[0] === 'BOX' ? 'Palco' :
                     parts[0] === 'PB' ? 'Planta Baja' : 
                     parts[0] === 'PP' ? 'Primer Piso' : parts[0];
      const row = parts[1];
      const seat = parts[2];
      return `${section} - Fila ${row} - Asiento ${seat}`;
    }
    return compositeId;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Detalles de la Operación
        </h2>
        <Button
          onClick={onClose}
          variant="ghost"
          className="p-1"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {operation && (
        <div className="space-y-6">
          {/* Información de la Operación */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Información de la Operación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tipo de Operación</label>
                  <p className={`font-medium ${getOperationTypeColor(operation.operationType)}`}>
                    {getOperationTypeText(operation.operationType)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Fecha de la Operación</label>
                  <p className="text-gray-900">{formatDate(operation.createdAt)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Total de Asientos</label>
                  <p className="text-gray-900 font-medium">{operation.totalSeats}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Monto Total</label>
                  <p className={`font-medium text-lg ${
                    operation.operationType === 'return_admin' 
                      ? 'text-red-600' 
                      : (operation.totalAmount > 0 ? 'text-green-600' : 'text-red-600')
                  }`}>
                    {operation.operationType === 'return_admin' 
                      ? formatPrice(operation.totalAmount) 
                      : formatPrice(Math.abs(operation.totalAmount))}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Descripción</label>
                <p className="text-gray-900">{operation.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Asientos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Asientos de la Operación ({operation.reservations.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {operation.reservations.map((reservation, index) => (
                  <div key={reservation.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Asiento</label>
                        <p className="text-gray-900 font-mono font-medium">
                          {formatSeatId(reservation.compositeId)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Precio</label>
                        <p className="text-gray-900 font-medium">
                          {formatPrice(reservation.price)}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Estado</label>
                        <div className="flex flex-col gap-1">
                          {/* Badge adicional para tipo de operación */}
                          {reservation.status === 'returned' && (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Devolución
                            </div>
                          )}
                          {reservation.status === 'sold' && (
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Venta
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Información del estudiante */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Alumno</label>
                          <p className="text-gray-900 font-medium">
                            {reservation.studentName || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Información del Usuario */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Usuario que Realizó la Operación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Usuario</label>
                  <p className="text-gray-900 font-medium">
                    {operation.userName || 'Sistema'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Sede</label>
                  <p className="text-gray-900">{operation.sede || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
        <Button onClick={onClose} variant="outline">
          Cerrar
        </Button>
      </div>
    </Modal>
  );
};

export default SeatDetailsModal;